/**
 * The document list layout shared by the Documents page folders and the
 * supplier Documents tab: a sortable Name / Date Uploaded header over rows that
 * carry the uploader, size and tags under the name, the uploaded date in its
 * own column, and quick actions. One definition, so both screens list and sort
 * documents the same way.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Download, Eye, Edit, Trash2, MoreVertical, User, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Document } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { getFileIcon } from './utils';

export type DocSortKey = 'name' | 'date';
export type SortDir = 'asc' | 'desc';

/**
 * Sort state for a document list: Name (A→Z first) or Date Uploaded (newest
 * first), toggling direction when the active column is clicked again.
 *
 * @param docs - The documents to sort (the full visible set, before paging)
 * @param onChange - Called when the sort changes (e.g. to return to page 1)
 * @returns The sort key/direction, the toggle, and the sorted documents
 */
export function useDocumentSort(docs: Document[], onChange?: () => void) {
  const [sortKey, setSortKey] = useState<DocSortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const toggleSort = (key: DocSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc'); // names A→Z, dates newest-first
    }
    onChange?.();
  };
  const sorted = useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const arr = [...docs];
    arr.sort((a, b) => {
      const cmp = sortKey === 'name'
        ? collator.compare(a.title || '', b.title || '')
        : new Date(a.version_uploaded_at || a.uploaded_at || 0).getTime()
          - new Date(b.version_uploaded_at || b.uploaded_at || 0).getTime();
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [docs, sortKey, sortDir]);
  return { sortKey, sortDir, toggleSort, sorted };
}

interface DocumentRowListProps {
  documents: Document[];
  /** Extra inline tags per row (folder, importance…) beside Public / acceptance. */
  renderBadges?: (doc: Document) => ReactNode;
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
  selectedDocIds?: number[];
  onToggleDocSelected?: (id: number) => void;
}

/**
 * Clickable column header for the document list. Toggles sort direction on
 * the active column; the parent owns the sort state so it can sort the full
 * folder before paginating.
 */
export function DocumentSortHeader({
  sortKey,
  sortDir,
  onSort,
  selectable = false,
  allSelected = false,
  someSelected = false,
  onToggleSelectAll,
}: {
  sortKey: DocSortKey;
  sortDir: SortDir;
  onSort: (key: DocSortKey) => void;
  selectable?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleSelectAll?: () => void;
}) {
  const arrow = (key: DocSortKey) =>
    sortKey !== key
      ? <ArrowUpDown className="h-3 w-3 opacity-40" />
      : sortDir === 'asc'
        ? <ArrowUp className="h-3 w-3" />
        : <ArrowDown className="h-3 w-3" />;
  return (
    <div className="flex items-center gap-3 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground border rounded-lg bg-muted/40">
      {/* Select-all checkbox (aligns with each row's select checkbox). */}
      {selectable && (
        <input
          type="checkbox"
          className="shrink-0 cursor-pointer"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={() => onToggleSelectAll?.()}
          aria-label={allSelected ? 'Deselect all documents' : 'Select all documents'}
          title={allSelected ? 'Deselect all' : 'Select all'}
        />
      )}
      {/* spacer aligns the Name header with the row's file icon */}
      <span className="w-5 shrink-0" aria-hidden="true" />
      <button
        type="button"
        onClick={() => onSort('name')}
        className="flex items-center gap-1 flex-1 min-w-0 hover:text-foreground"
      >
        Name {arrow('name')}
      </button>
      <button
        type="button"
        onClick={() => onSort('date')}
        className="flex items-center justify-end gap-1 w-28 shrink-0 hover:text-foreground"
      >
        Date Uploaded {arrow('date')}
      </button>
      {/* spacer aligns the header with the row's action buttons */}
      <span className="w-24 shrink-0" aria-hidden="true" />
    </div>
  );
}

export function DocumentRowList({ documents, onDownload, onDelete, onEdit, onPreview, onOpenDetail, selectedDocIds, onToggleDocSelected, renderBadges }: DocumentRowListProps) {
  return (
    <div className="divide-y border rounded-lg overflow-hidden">
      {documents.map((doc) => (
        <DocumentRow
          key={doc.id}
          doc={doc}
          onDownload={onDownload}
          onDelete={onDelete}
          onEdit={onEdit}
          onPreview={onPreview}
          onOpenDetail={onOpenDetail}
          selected={selectedDocIds?.includes(doc.id)}
          onToggleSelected={onToggleDocSelected}
          renderBadges={renderBadges}
        />
      ))}
    </div>
  );
}

interface DocumentRowProps {
  doc: Document;
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
  selected?: boolean;
  onToggleSelected?: (id: number) => void;
  renderBadges?: (doc: Document) => ReactNode;
}

export function DocumentRow({ doc, onDownload, onDelete, onEdit, onPreview, onOpenDetail, selected, onToggleSelected, renderBadges }: DocumentRowProps) {
  // CR-5330: Row body opens the details popup; the inline action
  // buttons + kebab menu stay quick actions. stopPropagation() on each
  // button keeps a button click from also bubbling into the row click.
  const { formatDate } = useFormatters();
  const handleRowClick = () => onOpenDetail?.(doc);
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 hover:bg-muted/40 transition-colors ${onOpenDetail ? 'cursor-pointer' : ''}`}
      onClick={onOpenDetail ? handleRowClick : undefined}
      role={onOpenDetail ? 'button' : undefined}
      tabIndex={onOpenDetail ? 0 : undefined}
      onKeyDown={onOpenDetail ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(doc); } } : undefined}
    >
      {onToggleSelected && (
        <input
          type="checkbox"
          checked={!!selected}
          onClick={stop}
          onChange={() => onToggleSelected(doc.id)}
          className="shrink-0 cursor-pointer"
          aria-label={`Select ${doc.title}`}
        />
      )}
      <div className="text-muted-foreground shrink-0">{getFileIcon(doc)}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" title={doc.title}>
          {doc.title}
        </p>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
          {doc.uploaded_by_name && (
            <span className="flex items-center gap-1 min-w-0">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{doc.uploaded_by_name}</span>
            </span>
          )}
          {doc.formatted_file_size && (
            <span className="tabular-nums">{doc.formatted_file_size}</span>
          )}
          {doc.is_public && (
            <Badge variant="outline" className="text-[10px] py-0 h-4">Public</Badge>
          )}
          {doc.requires_acceptance && (
            <Badge className="text-[10px] py-0 h-4 bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200">Requires acceptance</Badge>
          )}
          {renderBadges?.(doc)}
        </div>
      </div>
      {/* Date Uploaded column — aligns under the sortable header. Shows the
          CURRENT version's date, so a revised document reads as revised. */}
      <div className="w-28 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
        {doc.version_uploaded_at || doc.uploaded_at
          ? formatDate(doc.version_uploaded_at || doc.uploaded_at)
          : '—'}
      </div>
      <div className="flex items-center justify-end gap-1 w-24 shrink-0" onClick={stop}>
        {onPreview && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onPreview(doc)} title="Preview">
            <Eye className="h-4 w-4" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDownload(doc)} title="Download">
          <Download className="h-4 w-4" />
        </Button>
        {(onEdit || onDelete) && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7" title="More actions">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit && (
              <DropdownMenuItem onClick={() => onEdit(doc)}>
                <Edit className="h-4 w-4 mr-2" />Edit
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem className="text-red-600" onClick={() => onDelete(doc)}>
                <Trash2 className="h-4 w-4 mr-2" />Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>
    </div>
  );
}
